/**
 * Remplaçant d'imapflow pour le navigateur.
 *
 * scanner.js est le même fichier que dans l'application locale : il parle à
 * une classe ImapFlow. Celle-ci en reproduit la petite partie qu'il utilise
 * (connect, list, getMailboxLock, search, fetch, logout), au-dessus du tunnel
 * chiffré et du client IMAP maison.
 */
import { ouvrirTunnel } from './tunnel.js';
import { ClientImap, ErreurImap } from './imap/client.js';
import {
  chaine, estAscii, ensembleUid, dateRecherche, decoderUtf7, encoderUtf7, lireFetch, enTexte
} from './imap/syntaxe.js';

// Nombre d'UID par commande FETCH : une commande trop longue est refusée par
// certains serveurs, et des paquets réguliers font avancer la barre.
const PAQUET_FETCH = 500;

// Remplaçable par les tests, qui branchent un faux serveur IMAP.
let ouvrir = ouvrirTunnel;
export function remplacerTunnel(fonction) {
  ouvrir = fonction || ouvrirTunnel;
}

/** Réponse initiale XOAUTH2 : user=…^Aauth=Bearer …^A^A, en base64. */
export function xoauth2(utilisateur, jeton) {
  const octets = new TextEncoder().encode(`user=${utilisateur}\x01auth=Bearer ${jeton}\x01\x01`);
  let binaire = '';
  for (const o of octets) binaire += String.fromCharCode(o);
  return btoa(binaire);
}

const USAGES_SPECIAUX = ['\\All', '\\Archive', '\\Drafts', '\\Flagged', '\\Junk', '\\Sent', '\\Trash'];

export class ImapFlow {
  constructor(options) {
    this.options = options;
    this.client = null;
    this.capabilities = new Set();
    this.cheminsBruts = new Map(); // nom affiché -> nom tel que le serveur l'écrit
  }

  async connect() {
    const { host, port, auth } = this.options;
    if (Number(port) !== 993) throw new Error('Seul le port 993 (IMAP chiffré) est pris en charge.');
    const tunnel = await ouvrir(host);
    this.client = new ClientImap(tunnel);
    await this.client.accueil();
    try {
      if (auth.accessToken) {
        // Microsoft n'accepte plus les mots de passe : on présente le jeton
        // obtenu par « Se connecter avec Microsoft » (SASL XOAUTH2).
        await this.client.authentifier('XOAUTH2', xoauth2(auth.user, auth.accessToken));
      } else {
        await this.client.commande(['LOGIN ', chaine(auth.user), ' ', chaine(auth.pass)]);
      }
    } catch (err) {
      tunnel.fermer();
      if (err instanceof ErreurImap) {
        const echec = new Error(`Authentification refusée : ${err.message}`);
        echec.authenticationFailed = true;
        throw echec;
      }
      throw err;
    }
    // Les capacités changent souvent une fois connecté (X-GM-EXT-1 chez Gmail).
    await this.client.commande('CAPABILITY');
    this.capabilities = this.client.capacites;
  }

  async list() {
    const dossiers = [];
    for (const r of await this.client.commande('LIST "" "*"')) {
      if (!/^\* LIST /i.test(r.texte)) continue;
      const [, , attributs, , nom] = r.jetons();
      const brut = enTexte(nom);
      if (brut === null) continue;
      const flags = new Set((attributs || []).map(String));
      const chemin = decoderUtf7(brut);
      this.cheminsBruts.set(chemin, brut);
      dossiers.push({
        path: chemin,
        name: chemin,
        flags,
        specialUse: USAGES_SPECIAUX.find((u) => [...flags].some((f) => f.toLowerCase() === u.toLowerCase())) || null
      });
    }
    return dossiers;
  }

  /** Ouvre le dossier en lecture seule : le scan ne modifie jamais la boîte. */
  async getMailboxLock(chemin) {
    const brut = this.cheminsBruts.get(chemin) ?? encoderUtf7(chemin);
    await this.client.commande(['EXAMINE ', chaine(brut)]);
    return { release() {} };
  }

  /**
   * Critères compris : all, since, subject, from, header, gmraw — ceux
   * qu'utilise scanner.js. Renvoie la liste des UID.
   */
  async search(criteres) {
    const morceaux = [];
    let utf8 = false;
    const ajouter = (motCle, valeur) => {
      morceaux.push(` ${motCle}`);
      if (valeur === undefined) return;
      if (!estAscii(valeur)) utf8 = true;
      morceaux.push(' ', chaine(valeur));
    };

    if (criteres.all) morceaux.push(' ALL');
    if (criteres.since) morceaux.push(` SINCE ${dateRecherche(criteres.since)}`);
    if (criteres.subject !== undefined) ajouter('SUBJECT', criteres.subject);
    if (criteres.from !== undefined) ajouter('FROM', criteres.from);
    if (criteres.gmraw !== undefined) ajouter('X-GM-RAW', criteres.gmraw);
    for (const [nom, valeur] of Object.entries(criteres.header || {})) {
      morceaux.push(` HEADER ${nom}`, ' ', chaine(valeur));
    }
    if (!morceaux.length) morceaux.push(' ALL');

    const uids = [];
    const commande = ['UID SEARCH', ...(utf8 ? [' CHARSET UTF-8'] : []), ...morceaux];
    for (const r of await this.client.commande(commande)) {
      const m = r.texte.match(/^\* SEARCH(.*)$/i);
      if (m) for (const n of m[1].trim().split(/\s+/)) if (n) uids.push(Number(n));
    }
    return uids;
  }

  /** Itère sur les messages demandés, au fil de leur arrivée. */
  async *fetch(uids, champs) {
    const items = ['UID'];
    if (champs.internalDate || champs.envelope) items.push('INTERNALDATE');
    if (champs.envelope) items.push('ENVELOPE');
    if (champs.flags) items.push('FLAGS');
    if (champs.headers && champs.headers.length) {
      items.push(`BODY.PEEK[HEADER.FIELDS (${champs.headers.map((h) => h.toUpperCase()).join(' ')})]`);
    }

    const tries = [...uids].sort((a, b) => a - b);
    for (let i = 0; i < tries.length; i += PAQUET_FETCH) {
      const paquet = ensembleUid(tries.slice(i, i + PAQUET_FETCH));
      for await (const r of this.client.flux(`UID FETCH ${paquet} (${items.join(' ')})`)) {
        if (!/^\* \d+ FETCH /i.test(r.texte)) continue;
        const msg = lireFetch(r.jetons());
        // Une date d'en-tête illisible ne doit pas faire perdre la date : on
        // retombe sur la date de réception.
        if (msg.envelope && !msg.envelope.date) msg.envelope.date = msg.internalDate || null;
        if (msg.uid) yield msg;
      }
    }
  }

  async logout() {
    if (this.client) await this.client.deconnexion();
  }

  close() {
    if (this.client) this.client.tunnel.fermer();
  }
}
